import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const DEFAULT_TITLE = "ไม่มีสิทธิ์เข้าถึง";
const DEFAULT_DESCRIPTION =
  "คุณไม่มีสิทธิ์ในการเข้าถึงหน้านี้ กรุณาติดต่อผู้ดูแลระบบ";

interface PermissionDeniedPageProps {
  title?: string;
  description?: string;
  /** Hide the default "กลับหน้าหลัก" button (e.g. when the home page is also denied). */
  hideHomeButton?: boolean;
  /** Custom action(s) rendered in place of the default home button. */
  action?: ReactNode;
}

/**
 * Standard, app-wide full-page screen shown whenever the current role is not
 * allowed to see a route/feature — whether blocked by a route guard, the
 * app-level GDP gate, or an API 403. Replaces the normal page content so the
 * user never lands on a broken/skeleton page or a toast-only error.
 */
export default function PermissionDeniedPage({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  hideHomeButton = false,
  action,
}: PermissionDeniedPageProps) {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 220px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 2,
        px: 2,
      }}
    >
      <Box
        sx={{
          width: 96,
          height: 96,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: (theme) => `${theme.palette.primary.main}1A`,
          color: "primary.main",
        }}
      >
        <LockOutlinedIcon sx={{ fontSize: 48 }} />
      </Box>

      <Typography variant="h4" color="primary" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ maxWidth: 440 }}
      >
        {description}
      </Typography>

      {action
        ? action
        : !hideHomeButton && (
            <Button
              variant="contained"
              sx={{ mt: 1 }}
              onClick={() => navigate("/")}
            >
              กลับหน้าหลัก
            </Button>
          )}
    </Box>
  );
}
